import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { InnerPageLayout, InnerCTA } from "./InnerPageLayout";
import { RenderSection } from "./SectionRenderer";
import { INDUSTRIES } from "../data/pages";
import { Seo } from "../components/site/Seo";

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const IndustryPage = () => {
  const { slug } = useParams();
  const data = INDUSTRIES[slug];
  if (!data) return <Navigate to="/" replace />;

  return (
    <InnerPageLayout title={data.h1}>
      <Seo
        title={data.eyebrow}
        description={data.intro}
        path={`/industries/${slug}`}
        image={data.heroImage}
      />
      {/* Hero — asymmetric split with industry image */}
      <section
        id="top"
        data-testid="page-hero"
        className="relative overflow-hidden bg-[#050505] pt-16 md:pt-20 pb-16 md:pb-20"
      >
        <div className="absolute inset-0 bg-grid opacity-30" aria-hidden="true" />
        <div className="absolute inset-0 radial-spotlight" aria-hidden="true" />
        <div
          className="absolute -top-40 -right-32 w-[540px] h-[540px] rounded-full blur-[140px] opacity-20"
          style={{ background: "#FF5F1F" }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 font-mono text-[10px] md:text-xs uppercase tracking-[0.2em] text-zinc-500 mb-8">
            <Link to="/" className="hover:text-[#FF5F1F] transition-colors">
              Home
            </Link>
            <span className="text-zinc-700">/</span>
            <span>Industries</span>
            <span className="text-zinc-700">/</span>
            <span>{data.eyebrow}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
            {/* Left: type block */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-[#FF5F1F]" />
                <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
                  {data.eyebrow}
                </span>
              </div>

              <h1
                data-testid="page-h1"
                className="font-headings text-4xl sm:text-5xl md:text-6xl lg:text-[64px] font-black tracking-[-0.02em] leading-[1.02] text-white"
              >
                {data.h1}
              </h1>

              <p className="mt-8 max-w-2xl text-base md:text-lg text-zinc-400 leading-relaxed">
                {data.intro}
              </p>

              <div className="mt-8">
                <button
                  onClick={scrollToContact}
                  data-testid="hero-primary-cta"
                  className="group inline-flex items-center gap-2 bg-[#FF5F1F] hover:bg-[#E04F15] text-black font-bold px-7 py-4 text-sm md:text-base transition-all"
                >
                  {data.primaryCta || "Start a Conversation"}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>

            {/* Right: image block */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5"
              data-testid="page-hero-image"
            >
              <div className="relative aspect-[4/5] border border-white/10 overflow-hidden bg-black group">
                <img
                  src={data.heroImage}
                  alt={data.h1}
                  className="absolute inset-0 w-full h-full object-cover opacity-75 grayscale group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-[900ms]"
                  loading="eager"
                />
                {/* Gradient wash */}
                <div className="absolute inset-0 bg-gradient-to-tr from-black/80 via-black/20 to-transparent" />
                {/* Orange corner accents */}
                <div className="absolute top-4 left-4 flex items-center gap-2 z-10">
                  <span className="w-1.5 h-1.5 bg-[#FF5F1F] animate-accent-pulse" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80">
                    /INDUSTRY
                  </span>
                </div>
                <div className="absolute bottom-4 right-4 z-10 border-l-2 border-[#FF5F1F] pl-3">
                  <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/70">
                    Sector
                  </div>
                  <div className="font-headings text-sm md:text-base font-bold text-white leading-tight">
                    {data.eyebrow}
                  </div>
                </div>
                {/* Bottom-left brutalist tag */}
                <div className="absolute bottom-4 left-4 z-10 flex items-center gap-2">
                  <div className="w-2 h-2 border border-[#FF5F1F]" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                    Techovarya
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {data.sections.map((section, i) => (
        <RenderSection key={i} section={section} i={i} />
      ))}

      {/* Compliance note (per doc for healthcare) */}
      {data.slug === "healthcare-technology" && (
        <section
          className="relative bg-[#0A0A0A] border-t border-white/10 py-16 md:py-20"
          data-testid="compliance-note"
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="border border-[#FF5F1F]/40 bg-[#FF5F1F]/[0.03] p-6 md:p-8">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
                Compliance Note
              </span>
              <p className="mt-3 text-sm md:text-base text-zinc-300 leading-relaxed">
                Techovarya scopes compliance capability (HIPAA, GDPR, SOC 2, ISO, and related
                frameworks) per engagement — based on the specific processes, infrastructure,
                controls, and contractual responsibilities of each project. Contact us to
                discuss requirements for your product.
              </p>
            </div>
          </div>
        </section>
      )}

      <InnerCTA
        heading={data.finalCtaHeading}
        copy={data.finalCtaCopy}
        cta={data.finalCta}
        testid="final-cta"
      />
    </InnerPageLayout>
  );
};
